import type { ResourceCost } from '@kaeliux/shared';
import { formatCompactNumber } from '../../utils/formatters';
import { RESOURCE_SWATCHES, type SwatchResourceKey } from './resourceSwatches';

export interface CostChipsProps {
    cost: ResourceCost;
    /** Resource keys to render in the danger color instead of the default muted one — a shortfall highlight. */
    shortfallKeys?: SwatchResourceKey[];
    /** Prefix for each chip's `data-testid` (`${testIdPrefix}-${key}`) and the wrapper's own. Defaults to `cost-chip`. */
    testIdPrefix?: string;
}

const RESOURCE_KEYS = Object.keys(RESOURCE_SWATCHES) as SwatchResourceKey[];

const DEFAULT_TEST_ID_PREFIX = 'cost-chip';

const WRAPPER_CLASSES = 'flex flex-wrap gap-2.5';

const CHIP_CLASSES = 'flex items-center gap-1 text-label';

const SWATCH_CLASSES = 'h-2 w-2 rounded-badge';

const DEFAULT_TEXT_CLASSES = 'text-content-secondary';

const SHORTFALL_TEXT_CLASSES = 'text-status-danger';

/**
 * Per-resource cost chips (metal/minerals/gas/energy), shared by
 * `BuiltPlotContent`'s upgrade cost row and `PlotChooserContent`'s
 * per-building cost preview (DS-25) so both read the exact same swatches and
 * number formatting instead of carrying independent copies that can drift.
 */
export function CostChips({ cost, shortfallKeys = [], testIdPrefix = DEFAULT_TEST_ID_PREFIX }: CostChipsProps) {
    return (
        <div className={WRAPPER_CLASSES} data-testid={`${testIdPrefix}-row`}>
            {RESOURCE_KEYS.filter((key) => !!cost[key]).map((key) => (
                <span key={key} className={CHIP_CLASSES} data-testid={`${testIdPrefix}-${key}`}>
                    <span aria-hidden="true" className={SWATCH_CLASSES} style={{ background: RESOURCE_SWATCHES[key].color }} />
                    <span className={shortfallKeys.includes(key) ? SHORTFALL_TEXT_CLASSES : DEFAULT_TEXT_CLASSES}>
                        {formatCompactNumber(cost[key] ?? 0)} {RESOURCE_SWATCHES[key].label}
                    </span>
                </span>
            ))}
        </div>
    );
}

export default CostChips;
